import React, { useState, useRef, useEffect } from 'react';
import {
  EnvelopeIcon,
  PaperAirplaneIcon,
  DevicePhoneMobileIcon,
  PhoneIcon,
  ArrowPathIcon,
  ChatBubbleLeftIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  DocumentIcon,
  CalendarIcon,
  EllipsisHorizontalIcon,
  CheckCircleIcon,
  ArrowUturnLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  ArrowsRightLeftIcon,
  PaperClipIcon,
  UserIcon,
  XMarkIcon,
  CheckBadgeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import type { ActivityItemProps } from './ActivityItem.types';
import { ACTIVITY_TYPE_COLORS, DIRECTION_COLORS, TYPE_BADGE_LABELS } from './ActivityItem.types';
import type { CommunicationType } from '../../types/tracker.types';
import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toDisplayFormat } from '../InlineQuickComposer/mentionUtils';
import './ActivityItem.styles.css';

/**
 * Confirmation popover for destructive actions
 */
const ConfirmPopover: React.FC<{
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ message, onConfirm, onCancel }) => (
  <div className="activity-confirm-popover">
    <span className="activity-confirm-message">{message}</span>
    <div className="activity-confirm-actions">
      <button className="activity-confirm-cancel" onClick={onCancel}>
        Annuler
      </button>
      <button className="activity-confirm-confirm" onClick={onConfirm}>
        Confirmer
      </button>
    </div>
  </div>
);

/**
 * Get the icon component for a communication type
 */
const getTypeIcon = (type: CommunicationType): React.ReactNode => {
  switch (type) {
    case 'email':
      return <EnvelopeIcon />;
    case 'sms_outbox':
      return <PaperAirplaneIcon />;
    case 'sms_inbox':
      return <DevicePhoneMobileIcon />;
    case 'status_update':
      return <ArrowPathIcon />;
    case 'tracker_com':
      return <ChatBubbleLeftIcon />;
    case 'com_internes':
      return <ChatBubbleLeftRightIcon />;
    case 'com_post_it':
      return <DocumentTextIcon />;
    case 'com_avant_envoyer':
      return <DocumentIcon />;
    case 'rdv':
      return <CalendarIcon />;
    case 'voip_call':
      return <PhoneIcon />;
    default:
      return <DocumentIcon />;
  }
};

/**
 * Get type-specific styles (colors) for the timeline dot
 */
const getTypeStyles = (type: CommunicationType): React.CSSProperties => {
  const styles: Record<CommunicationType, { bg: string; color: string }> = {
    email: { bg: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' },
    sms_outbox: { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981' },
    sms_inbox: { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981' },
    status_update: { bg: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' },
    tracker_com: { bg: 'rgba(239, 83, 67, 0.12)', color: '#ef5343' },
    com_internes: { bg: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' },
    com_post_it: { bg: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24' },
    com_avant_envoyer: { bg: 'rgba(100, 116, 139, 0.12)', color: '#64748b' },
    rdv: { bg: 'rgba(14, 165, 233, 0.12)', color: '#0ea5e9' },
  };
  const typeStyle = styles[type] || styles.com_avant_envoyer;
  return {
    '--activity-type-bg': typeStyle.bg,
    '--activity-type-color': typeStyle.color,
  } as React.CSSProperties;
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (date: Date): string => {
  if (isToday(date)) {
    return format(date, 'HH:mm', { locale: fr });
  }
  if (isYesterday(date)) {
    return `Hier ${format(date, 'HH:mm', { locale: fr })}`;
  }
  return format(date, 'dd/MM HH:mm', { locale: fr });
};

/**
 * Get urgency label
 */
const getUrgencyLabel = (urgency: string): string => {
  switch (urgency) {
    case 'urgent':
      return 'Urgent';
    case 'high':
      return 'Haute';
    case 'medium':
      return 'Moyenne';
    case 'low':
      return 'Basse';
    default:
      return '';
  }
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * ActivityItem Component
 *
 * A unified activity item component that handles all communication types
 * with consistent structure: icon, title, meta, snippet, and quick actions.
 */
export const ActivityItem: React.FC<ActivityItemProps> = ({
  type,
  id,
  title,
  meta,
  snippet,
  fullContent,
  htmlContent,
  timestamp,
  isExpanded: controlledExpanded,
  onToggleExpand,
  urgency,
  isDone,
  doneBy,
  doneAt,
  doneComment,
  isHighlighted,
  isUnread,
  author,
  authorId,
  currentUserId,
  assignedTo,
  assignedToId,
  planValidatedByName,
  direction,
  recipient,
  attachments,
  onMarkDone,
  onEdit,
  onDelete,
  onViewFull,
  onReallocate,
  onReply,
  isEdited,
  editedAt,
  quickActions = [],
  children,
  className = '',
  recordingUrl,
  durationMs,
  voipContactName,
  voipContactRoles,
  voipAgentName,
  voipSummary,
  voipTranscription,
  voipProcessedAt,
}) => {
  // Internal expanded state (for uncontrolled mode)
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isExpanded = controlledExpanded ?? internalExpanded;

  // Overflow menu state
  const [showOverflow, setShowOverflow] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmComment, setConfirmComment] = useState('');
  const confirmRef = useRef<HTMLDivElement>(null);

  // Close overflow menu on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) {
        setShowOverflow(false);
      }
      if (confirmRef.current && !confirmRef.current.contains(event.target as Node)) {
        setShowConfirm(false);
      }
    };

    if (showOverflow || showConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOverflow, showConfirm]);

  // Detect when the collapsed snippet is visually cut by the CSS line-clamp:
  // messages under the adapter's 150-char truncation have snippet === fullContent,
  // yet the clamp can still hide part of them.
  const [isSnippetClamped, setIsSnippetClamped] = useState(false);
  const snippetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = snippetRef.current;
    if (!el) return;
    const measure = () => setIsSnippetClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [snippet, isExpanded, isDone, doneComment]);

  // Check if item has expandable content (more content than snippet)
  // Expandable if fullContent is different/longer than snippet, has HTML/children,
  // or the snippet itself is visually clamped
  const hasExpandableContent = !!(
    htmlContent ||
    children ||
    (fullContent && fullContent !== snippet && fullContent.length > (snippet?.length || 0)) ||
    (snippet && isSnippetClamped)
  );

  // Item is clickable only for emails (modal) or if there's content to expand
  const isClickable = (type === 'email' && onViewFull) || hasExpandableContent;

  // Toggle expanded state (controlled or uncontrolled)
  const toggleExpanded = () => {
    if (onToggleExpand) {
      onToggleExpand();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  // Handle click on the item
  const handleClick = (e: React.MouseEvent) => {
    // Don't do anything if not clickable
    if (!isClickable) {
      return;
    }

    // Don't toggle if clicking on actions or overflow
    if ((e.target as HTMLElement).closest('.activity-item-header-actions') ||
        (e.target as HTMLElement).closest('.activity-item-overflow') ||
        (e.target as HTMLElement).closest('.activity-item-overflow-menu') ||
        (e.target as HTMLElement).closest('.activity-confirm-popover') ||
        (e.target as HTMLElement).closest('.activity-item-terminer-btn') ||
        (e.target as HTMLElement).closest('.activity-item-rouvrir-btn')) {
      return;
    }

    // For emails, open the modal instead of expanding inline
    if (type === 'email' && onViewFull) {
      onViewFull();
      return;
    }

    // Only expand if there's content to show
    if (hasExpandableContent) {
      toggleExpanded();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter to expand/toggle
    if (e.key === 'Enter' && isClickable) {
      e.preventDefault();
      // For emails, open the modal
      if (type === 'email' && onViewFull) {
        onViewFull();
        return;
      }
      // Toggle expand for other types
      if (hasExpandableContent) {
        toggleExpanded();
      }
    }
    // Escape to collapse
    if (e.key === 'Escape' && isExpanded) {
      e.preventDefault();
      if (onToggleExpand) {
        onToggleExpand();
      } else {
        setInternalExpanded(false);
      }
    }
  };

  // Handle confirm done with optional comment
  const handleConfirmDone = () => {
    if (onMarkDone) {
      onMarkDone(id, true, confirmComment || undefined);
    }
    setShowConfirm(false);
    setConfirmComment('');
  };

  // Handle cancel confirm
  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setConfirmComment('');
  };

  // Get type color - use direction color if available, otherwise type color
  const typeColor = ACTIVITY_TYPE_COLORS[type] || '#e5e7eb';
  const effectiveColor = direction ? DIRECTION_COLORS[direction] : typeColor;

  // Check if current user can edit/delete (tracker_com or com_post_it)
  const canEdit = (type === 'tracker_com' || type === 'com_post_it') && authorId && currentUserId && authorId === currentUserId;

  // Check if current user is the one assigned (has a pending todo)
  const isAssignedToCurrentUser = !isDone && assignedToId && currentUserId && assignedToId === currentUserId;

  /**
   * Get inline actions based on type (max 2)
   * Returns: { primary: { label, icon, onClick, tooltip, highlight? }, secondary: { ... } | null }
   */
  const getInlineActions = () => {
    const actions: { label: string; icon: React.ReactNode; onClick: () => void; tooltip: string; highlight?: boolean }[] = [];

    // Type-specific primary action
    // Note: Terminer button for tracker_com/com_internes is now in a separate footer section
    switch (type) {
      case 'tracker_com':
      case 'com_internes':
        // No inline action - Terminer is shown as a prominent bottom button
        break;

      case 'com_post_it':
        // Post-it: Modifier
        if (canEdit && onEdit) {
          actions.push({
            label: 'Modifier',
            icon: <PencilSquareIcon />,
            onClick: onEdit,
            tooltip: 'Modifier',
          });
        }
        break;

      case 'status_update':
        // Statut: Voir
        if (onViewFull) {
          actions.push({
            label: 'Voir',
            icon: <EyeIcon />,
            onClick: onViewFull,
            tooltip: 'Voir les détails',
          });
        }
        break;

      default:
        // No default action
        break;
    }

    // Note: Rouvrir button for tracker_com/com_internes is now in the footer section

    return actions.slice(0, 2); // Max 2 inline actions
  };

  /**
   * Get overflow menu items
   */
  const getOverflowItems = () => {
    const items: { label: string; icon: React.ReactNode; onClick: () => void; danger?: boolean }[] = [];

    // View full (for emails)
    if (onViewFull && type === 'email') {
      items.push({
        label: 'Voir le contenu complet',
        icon: <EyeIcon />,
        onClick: onViewFull,
      });
    }

    // Reply (for received emails only)
    if (onReply && type === 'email') {
      items.push({
        label: 'Répondre',
        icon: <ArrowUturnLeftIcon />,
        onClick: onReply,
      });
    }

    // Reallocate (for emails and SMS inbox)
    if (onReallocate && (type === 'email' || type === 'sms_inbox')) {
      items.push({
        label: 'Réattribuer',
        icon: <ArrowsRightLeftIcon />,
        onClick: onReallocate,
      });
    }

    // Edit (for tracker_com by author)
    if (canEdit && onEdit && type !== 'com_post_it') {
      items.push({
        label: 'Modifier',
        icon: <PencilSquareIcon />,
        onClick: onEdit,
      });
    }

    // Delete (for tracker_com by author)
    if (canEdit && onDelete) {
      items.push({
        label: 'Supprimer',
        icon: <TrashIcon />,
        onClick: onDelete,
        danger: true,
      });
    }

    // Additional quick actions
    quickActions.forEach((action) => {
      items.push({
        label: action.label,
        icon: null,
        onClick: action.onClick,
        danger: action.variant === 'danger',
      });
    });

    return items;
  };

  const inlineActions = getInlineActions();
  const overflowItems = getOverflowItems();

  // CSS variables for type color (uses direction color when available)
  const style = {
    '--activity-type-color': effectiveColor,
    '--activity-type-bg': `${effectiveColor}15`,
  } as React.CSSProperties;

  // Render status_update as a simpler timeline item
  if (type === 'status_update') {
    return (
      <div className="activity-status-item" style={style}>
        <div className="activity-status-marker">
          <div className="activity-status-dot" />
          <div className="activity-status-line" />
        </div>
        <div className="activity-status-content">
          <div className="activity-status-badge">
            {title}
          </div>
          <span className="activity-status-time">
            {format(timestamp, 'HH:mm', { locale: fr })}
          </span>
        </div>
      </div>
    );
  }

  // Build class names for regular items
  const classNames = [
    'activity-item',
    isExpanded && 'expanded',
    isDone && 'done',
    isHighlighted && 'highlighted',
    isUnread && 'unread',
    isClickable && 'clickable',
    direction && `direction-${direction}`,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classNames}
      style={{ ...style, ...getTypeStyles(type) }}
      onClick={isClickable ? handleClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-expanded={isClickable ? isExpanded : undefined}
    >
      <div className="activity-item-header">
        {/* Icon + Type Badge */}
        <div className="activity-item-icon-group">
          <div className="activity-item-icon">
            {getTypeIcon(type)}
          </div>
          <span className="activity-item-type-badge">
            {TYPE_BADGE_LABELS[type]}
          </span>
        </div>

        {/* Content */}
        <div className="activity-item-content">
          {/* Title row */}
          <div className="activity-item-title-row">
            <h4 className="activity-item-title">{title}</h4>
            {/* SMS sender/receiver info */}
            {type === 'sms_outbox' && recipient && (
              <span className="activity-item-sms-contact outbound">
                <span className="activity-item-sms-label">À:</span>
                <span className="activity-item-sms-value">{recipient}</span>
              </span>
            )}
            {type === 'sms_inbox' && author && (
              <span className="activity-item-sms-contact inbound">
                <span className="activity-item-sms-label">De:</span>
                <span className="activity-item-sms-value">{author}</span>
              </span>
            )}
            {isAssignedToCurrentUser && (type === 'tracker_com' || type === 'com_internes') && (
              <span className="activity-item-todo-tag">
                À traiter
              </span>
            )}
            {urgency && urgency !== 'low' && (
              <span className={`activity-item-urgency ${urgency}`}>
                {getUrgencyLabel(urgency)}
              </span>
            )}
            {assignedTo && (
              <span className="activity-item-assigned">
                <UserIcon className="w-3 h-3" />
                {assignedTo}
              </span>
            )}
            {planValidatedByName && (
              <span className="activity-item-plan-validated" title={`Plan Léa accepté par ${planValidatedByName}`}>
                <CheckBadgeIcon className="w-3 h-3" />
                Accepté par {planValidatedByName}
              </span>
            )}
          </div>

          {/* VoIP call card — caller info chip-row + audio player +
              GPT summary + collapsible full transcription. The audio
              stream is on the VoIP control-plane; we append the
              chatapp JWT as ?token= since <audio> can't send Auth
              headers. */}
          {type === 'voip_call' && (() => {
            const tok = (typeof window !== 'undefined' && window.localStorage.getItem('authToken')) || '';
            const playableUrl = recordingUrl
              ? `${recordingUrl}${recordingUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(tok)}`
              : null;
            const durationLabel = durationMs
              ? `${Math.floor(durationMs / 60000)}:${String(Math.floor((durationMs % 60000) / 1000)).padStart(2, '0')}`
              : null;
            const processingPending = !!recordingUrl && !voipProcessedAt && !voipSummary;
            return (
              <div className="activity-item-voip">
                {/* Meta row: caller roles, agent, duration */}
                <div className="activity-item-voip__chips">
                  {voipContactRoles && (
                    <span className="activity-item-voip__chip activity-item-voip__chip--role">
                      {voipContactRoles}
                    </span>
                  )}
                  {voipAgentName && (
                    <span className="activity-item-voip__chip activity-item-voip__chip--agent">
                      👤 {voipAgentName}
                    </span>
                  )}
                  {durationLabel && (
                    <span className="activity-item-voip__chip activity-item-voip__chip--duration">
                      ⏱ {durationLabel}
                    </span>
                  )}
                </div>

                {/* GPT summary — primary affordance, rendered before
                    the audio so the agent reads it first. */}
                {voipSummary && (
                  <div className="activity-item-voip__summary">
                    <span className="activity-item-voip__summary-label">✨ Résumé IA</span>
                    <p>{voipSummary}</p>
                  </div>
                )}

                {/* Pending state — summary not yet ready. */}
                {processingPending && (
                  <div className="activity-item-voip__pending">
                    ⏳ Résumé en cours…
                  </div>
                )}

                {/* Secondary actions — compact buttons that expand
                    inline. Defaults to closed so the summary stays
                    the primary read. */}
                {(playableUrl || voipTranscription) && (
                  <div className="activity-item-voip__actions">
                    {playableUrl && (
                      <details className="activity-item-voip__action">
                        <summary>🎧 Écouter</summary>
                        <div className="activity-item-voip__action-panel">
                          <audio controls preload="none" src={playableUrl} className="activity-item-voip__audio-el">
                            Votre navigateur ne supporte pas l'élément audio.
                          </audio>
                        </div>
                      </details>
                    )}
                    {voipTranscription && (
                      <details className="activity-item-voip__action">
                        <summary>📝 Transcription</summary>
                        <div className="activity-item-voip__action-panel">
                          <p>{voipTranscription}</p>
                        </div>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Snippet row with Terminer button */}
          {!isExpanded && (
            <div className="activity-item-snippet-row">
              {snippet && (
                <div className="activity-item-snippet" ref={snippetRef}>{toDisplayFormat(snippet)}</div>
              )}
              {/* Terminer/Rouvrir button - only for assigned user */}
              {(type === 'tracker_com' || type === 'com_internes') && onMarkDone && !showConfirm && (
                ((!isDone && isAssignedToCurrentUser) || (isDone && assignedToId && currentUserId && assignedToId === currentUserId)) && (
                  <>
                    {!isDone && isAssignedToCurrentUser ? (
                      <button
                        className="activity-item-terminer-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConfirm(true);
                        }}
                        type="button"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Terminer</span>
                      </button>
                    ) : (
                      <button
                        className="activity-item-rouvrir-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkDone(id, false);
                          setConfirmComment(doneComment || '');
                          setShowConfirm(true);
                        }}
                        type="button"
                      >
                        <ArrowUturnLeftIcon className="w-4 h-4" />
                        <span>Rouvrir</span>
                      </button>
                    )}
                  </>
                )
              )}
            </div>
          )}

          {/* Explicit expand button — visible whenever content is cut off */}
          {!isExpanded && hasExpandableContent && (
            <button
              className="activity-item-expand-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (type === 'email' && onViewFull) {
                  onViewFull();
                  return;
                }
                toggleExpanded();
              }}
              type="button"
            >
              <ChevronDownIcon className="w-3.5 h-3.5" />
              <span>Voir plus</span>
            </button>
          )}

          {/* Done comment shown when item is closed/done */}
          {isDone && doneComment && !isExpanded && !showConfirm && (
            <div className="activity-item-done-comment">
              <CheckCircleSolidIcon className="w-3.5 h-3.5" />
              <span>{doneComment}</span>
            </div>
          )}

          {/* Inline comment input when confirming */}
          {showConfirm && (
            <div className="activity-item-inline-confirm" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                className="activity-item-inline-input"
                placeholder="Commentaire (optionnel)..."
                value={confirmComment}
                onChange={(e) => setConfirmComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmDone();
                  if (e.key === 'Escape') handleCancelConfirm();
                }}
                autoFocus
              />
              <button
                className="activity-item-inline-cancel"
                onClick={handleCancelConfirm}
                type="button"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
              <button
                className="activity-item-inline-submit"
                onClick={handleConfirmDone}
                type="button"
              >
                <CheckCircleIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Right side: timestamp → actions → overflow */}
        <div className="activity-item-right">
          <span className="activity-item-timestamp">
            {formatTimestamp(timestamp)}
            {isEdited && (
              <span className="activity-item-edited" title={editedAt ? `Modifié le ${format(editedAt, 'dd/MM à HH:mm', { locale: fr })}` : 'Modifié'}>
                (modifié)
              </span>
            )}
          </span>

          {/* Inline actions (always visible) */}
          <div className="activity-item-header-actions">
            {inlineActions.map((action, index) => (
              <button
                key={index}
                className={`activity-header-action-btn${action.highlight ? ' highlight' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                title={action.tooltip}
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            ))}

          </div>

          {/* Overflow menu (always visible) */}
          {overflowItems.length > 0 && (
            <div className="activity-item-overflow-container" ref={overflowRef}>
              <button
                className="activity-item-overflow"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOverflow(!showOverflow);
                }}
                aria-label="Plus d'options"
              >
                <EllipsisHorizontalIcon className="w-5 h-5" />
              </button>

              {showOverflow && (
                <div className="activity-item-overflow-menu">
                  {overflowItems.map((item, index) => (
                    <button
                      key={index}
                      className={item.danger ? 'danger' : ''}
                      onClick={(e) => {
                        e.stopPropagation();
                        item.onClick();
                        setShowOverflow(false);
                      }}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="activity-item-expanded-content">
          {/* Full text content (falls back to snippet when only clamped by CSS) */}
          {(fullContent || snippet) && !htmlContent && (
            <div className="activity-item-full-content">{toDisplayFormat(fullContent || snippet || '')}</div>
          )}

          {/* HTML content (for emails) */}
          {htmlContent && (
            <div
              className="activity-item-html-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          )}

          {/* Children (custom content) */}
          {children}

          {/* Attachments */}
          {attachments && attachments.length > 0 && (
            <div className="activity-item-attachments">
              <div className="activity-item-attachments-title">
                <PaperClipIcon className="w-4 h-4" />
                Pièces jointes ({attachments.length})
              </div>
              <div className="activity-item-attachments-list">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="activity-item-attachment">
                    <PaperClipIcon />
                    <span>{attachment.name}</span>
                    <span className="text-gray-400">
                      ({formatFileSize(attachment.size)})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Done badge */}
          {isDone && doneBy && (
            <div className="activity-item-done-badge">
              <CheckCircleSolidIcon />
              <span>
                Terminé par {doneBy}
                {doneAt && ` le ${format(doneAt, 'dd/MM à HH:mm', { locale: fr })}`}
                {doneComment && ` — ${doneComment}`}
              </span>
            </div>
          )}

          {/* Collapse button */}
          <button
            className="activity-item-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
            type="button"
          >
            <ChevronUpIcon className="w-3.5 h-3.5" />
            <span>Voir moins</span>
          </button>
        </div>
      )}

    </div>
  );
};

export default ActivityItem;
